import { AfterViewInit, Component, computed, inject, OnInit, ViewChild, viewChild, ViewEncapsulation } from '@angular/core';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';
import { EnpsComponent } from '@common/components/base/enps-component';
import { ErrorLS } from '@common/data-models/misc/errors';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { AppName } from '@src/suite/common/enums/enums';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import {
    selectIsManagePublicationChannels,
    selectIsManageTeamMembers,
    selectIsPlanningViews,
    selectIsTemplates,
    selectIsWastebin,
} from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentApplication, selectCurrentViewState, selectFilterElevation, selectViewHasFilter } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AppService } from '@suite/app.service';
import { ClipboardComponent } from '@suite/platform/components/clipboard/clipboard';

import { CastObjectsComponent } from '../../../common/components/cast-objects/cast-objects';
import { FilterComponent } from '../../../common/components/filter/filter/filter';
import { AppSelectorComponent } from '../app-selector/app-selector';
import { MainContentComponent } from '../main-content/main-content.component';
import { SideBarComponent } from '../side-bar/side-bar';
import { TopBarComponent } from '../top-bar/top-bar';

@Component({
    selector: 'st-main',
    imports: [AppSelectorComponent, CastObjectsComponent, ClipboardComponent, FilterComponent, MainContentComponent, MatSidenavModule, RouterOutlet, SideBarComponent, TopBarComponent],
    templateUrl: './main.html',
    styleUrl: './main.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-main',
    },
})
export class MainComponent extends EnpsComponent implements OnInit, AfterViewInit {
    appService = inject(AppService);
    nomWebServicesJson = inject(NomWebServicesJson);
    navigationService = inject(NavigationService);
    globalTablesService = inject(GlobalTablesService);
    visualStateService = inject(VisualStateService);
    languageSettings = inject(LanguageSettings);
    store = inject(Store);

    readonly sideNav = viewChild<MatSidenav>('sideNav');
    readonly filter = viewChild<MatSidenav>('filter');

    // TODO: Skipped for migration because:
    //  Accessor queries cannot be migrated as they are too complex.
    // eslint-disable-next-line @angular-eslint/prefer-signals
    @ViewChild('clipboard')
    set clipboard(v: ClipboardComponent) {
        this.appService.clipboard = v;
    }
    get clipboard(): ClipboardComponent {
        return this.appService?.clipboard;
    }

    readonly appSelectorVisible = computed(() => {
        return !this.visualStateService.isLessThanSmall();
    });

    readonly isCastView = computed(() => this.application() === AppName.CAST);

    readonly sideNavMode = computed<MatDrawerMode>(() => {
        if (this.visualStateService.shouldPanelsFloat()) return 'over';
        return 'side';
    });

    readonly sideBarVisible = computed(() => this.application() !== AppName.EMBEDDED);

    viewStateHasFilter = this.store.selectSignal(selectViewHasFilter);
    isPlanningViews = this.store.selectSignal(selectIsPlanningViews);
    isManageTeamMembers = this.store.selectSignal(selectIsManageTeamMembers);
    isManagePublicationChannels = this.store.selectSignal(selectIsManagePublicationChannels);
    readonly viewHasFilter = computed(() => this.viewStateHasFilter() || this.isManagePublicationChannels() || this.isManageTeamMembers() || this.isPlanningViews());

    readonly isFilterOpened = computed(() => {
        return this.vs()?.PSTViewStateFilterState && !this.visualStateService.shouldPanelsFloat();
    });

    readonly isSidenavOpened = computed(() => {
        if (this.isWastebinOpen() || this.isTemplatesOpen()) {
            return false;
        }
        return !this.visualStateService.shouldPanelsFloat();
    });

    public loaderLabel = '';
    public newViewShown = false;
    public newTemplateShown = false;
    public productLabel: string = this.languageSettings.PSTWebApPlaybookLabel;
    public app_version = '';
    public isCopy = false;
    public templateRecord: any;
    public hasErrors = false;
    private warningErrors: ErrorLS[];
    private criticalErrors: ErrorLS[];

    filterElevation = this.store.selectSignal(selectFilterElevation);
    isWastebinOpen = this.store.selectSignal(selectIsWastebin);
    isTemplatesOpen = this.store.selectSignal(selectIsTemplates);

    ngOnInit() {
        if (environment.pst_version) {
            this.app_version = environment.pst_version.toLowerCase();
            this.globalTablesService.AppVersion = this.app_version;
            this.globalTablesService.setPlaybookHelpUrl();
            this.globalTablesService.setEnforceRequiredFieldsOnCreate();
        }
    }

    application = this.store.selectSignal(selectCurrentApplication);
    vs = this.store.selectSignal(selectCurrentViewState);

    ngAfterViewInit() {
        if (this.isWastebinOpen() || this.isTemplatesOpen()) {
            this.closeSideNav();
        }
        window['walkMeID'] = this.nomWebServicesJson.HelloResults.WalkMe;
    }

    public downloadErrors() {
        let text = '';
        if (this.criticalErrors && this.criticalErrors.length > 0) {
            text += 'CRITICAL:\n\r\n\r';
            for (const error of this.criticalErrors) {
                const msg = error.msg.replace(/[\n\r+]/gi, '');
                text += `[${error.timestamp}] - ${error.type.toUpperCase()} - ${msg}\n\r\n\r`;
            }
        }

        if (this.warningErrors && this.warningErrors.length > 0) {
            if (this.criticalErrors && this.criticalErrors.length > 0) {
                text += '\n\r\n\r------------------------------------------------------------------------------------\n\r\n\r\n\r\n\r';
            }
            text += 'WARNING:\n\r\n\r';
            for (const error of this.warningErrors) {
                const msg = error.msg.replace(/[\n\r+]/gi, '');
                text += `[${error.timestamp}] - ${error.type.toUpperCase()} - ${msg}\n\r\n\r`;
            }
        }

        const data = new Blob([text], { type: 'text/plain' });
        const element: HTMLAnchorElement = document.createElement<'a'>('a');
        element.download = 'errors.log';
        const textFile = window.URL.createObjectURL(data);
        element.href = textFile;
        element.click();
    }

    async toggleSideNav(): Promise<void> {
        if (this.isWastebinOpen() || this.isTemplatesOpen()) {
            return;
        }
        const res = await this.sideNav()?.toggle();
        if (res === 'open') {
            this.appService.sideBarExpanded.set(true);
        } else {
            this.appService.sideBarExpanded.set(false);
        }
    }

    async closeSideNav(): Promise<void> {
        await this.sideNav()?.close();
        this.appService.sideBarExpanded.set(false);
    }

    toggleFilter(): void {
        this.filter()?.toggle();
    }

    filterOpenedChange(event: boolean) {
        if (this.vs()?.PSTViewStateFilterState !== event) {
            this.store.dispatch(ViewStateActions.updateViewStateFilterState({ opened: event }));
        }
    }

    calculateInitialFilterState() {
        if (this.visualStateService.shouldPanelsFloat()) {
            this.filter()?.close();
            this.sideNav()?.close();
            return;
        }
        if (this.vs()?.PSTViewStateFilterState && (this.viewHasFilter() || this.isCastView)) {
            this.filter()?.open();
        } else {
            this.filter()?.close();
        }
    }
}
